import React, { useEffect, useState } from "react";
import banner from "../../images/Rectangle.png";
import binaceImage from "../../images/binace.png";
import { Tabs, Tab, useDisclosure, Button } from "@heroui/react";
import CollectableTabs from "./collectable-tab";
import OnsaleTab from "./onsale-tab";
import TrackAssetTabs from "./track-assets-tab";
import StakingTabs from "./staking-tabs";
import { useDispatch, useSelector } from "react-redux";
import {
  getUser,
  isUserLoggedIn,
  loginSuccess
} from "../../redux/reducer/user_reducer";
import { formatWalletAddress } from "../../utils/common";
import { useLocation, useNavigate } from "react-router-dom";
import ProfileEditModal from "./edit-profile";
import { PiUserBold } from "react-icons/pi";

const ProfileComponents = () => {
  const [activeTab, setActiveTab] = useState("collectable");
  const [isAuthChecked, setIsAuthChecked] = useState(false);
  const user = useSelector(getUser);
  const navigate = useNavigate();
  const isUserLogged = useSelector(isUserLoggedIn);
  const location = useLocation();
  const { isOpen, onOpen, onClose, onOpenChange } = useDisclosure();
  const dispatch = useDispatch();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get("tab");

    if (tabParam) {
      setActiveTab(tabParam);
    } else {
      setActiveTab("collectable");
    }
  }, [location.search]);

  const handleTabChange = (key) => {
    setActiveTab(key);
    const searchParams = new URLSearchParams(location.search);
    searchParams.set("tab", key);
    if (key !== "staking") {
      searchParams.delete("staking_type");
    }
    navigate({ search: searchParams.toString() }, { replace: true });
  };

  useEffect(() => {
    if (isAuthChecked && isUserLogged === false) {
      navigate("/");
    }
  }, [isUserLogged, navigate, isAuthChecked]);

  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        setIsAuthChecked(true);
      } catch (error) {
        console.error("Authentication check failed", error);
        setIsAuthChecked(true);
      }
    };

    checkAuthStatus();
  }, []);

  const handleProfileSave = (data) => {
    onClose();
    dispatch(loginSuccess(data));
  };

  return (
    <>
      <div className="marketplace-banner">
        <div className="banner-image relative">
          {user?.banner_image ? (
            <img
              src={user?.banner_image}
              className="w-full h-[13rem] md:h-[16rem] object-cover"
              alt="not-found"
            />
          ) : (
            <img
              src={banner}
              className="w-full h-[13rem] md:h-[16rem] object-cover"
              alt="not-found"
            />
          )}

          <Button
            onPress={onOpen}
            startContent={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="size-6"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10"
                />
              </svg>
            }
            className="absolute top-4 right-4 bg-yellowbuy text-black font-semibold px-4 py-2 rounded-lg shadow-md hover:bg-yellow-500 transition-all duration-200"
          >
            Edit Profile
          </Button>
        </div>

        <div className="profile-wrapper max-w-7xl mx-auto p-4 relative">
          <div className="flex flex-col items-center w-fit gap-4">
            <div className="profile-image mt-[-8rem] md:mt-[-10.5rem]">
              {user?.profile_image ? (
                <div className="w-[9rem] h-[9rem] md:w-[12rem] md:h-[12rem] bg-white rounded-full border-4 border-black">
                  <img
                    src={user?.profile_image}
                    className="w-full h-full object-cover rounded-full"
                    alt="not-found"
                  />
                </div>
              ) : (
                <div className="w-[9rem] h-[9rem] md:w-[12rem] md:h-[12rem] bg-white rounded-full text-black border-4 border-black flex items-center justify-center">
                  <PiUserBold className="w-20 h-20" />
                </div>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <h1 className="m-0 text-xl text-black font-bold">
                {`${
                  user?.first_name?.length > 12
                    ? user.first_name.slice(0, 12) + "..."
                    : user?.first_name || ""
                } ${
                  user?.last_name?.length > 10
                    ? user.last_name.slice(0, 10) + "..."
                    : user?.last_name || ""
                }`}
              </h1>
              <div className="flex items-center gap-2">
                <img
                  src={binaceImage}
                  className="w-6 h-6 object-contain"
                  alt="not-found"
                />
                <p className="m-0 text-base text-[#000000]">
                  {formatWalletAddress(user?.address)}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex w-full flex-col max-w-7xl mx-auto px-4 mt-4">
          <Tabs
            aria-label="Options"
            selectedKey={activeTab}
            onSelectionChange={handleTabChange}
            classNames={{
              tabList: "rounded-none p-0 bg-transparent",
              tab: "h-11",
              cursor:
                "group-data-[selected=true]:!bg-yellowbuy border border-black text-black font-bold shadow-none rounded-lg",
              tabContent: "text-black font-bold text-xl"
            }}
          >
            <Tab key="collectable" title="Collectable">
              <CollectableTabs />
            </Tab>
            <Tab key="onsale" title="On sale">
              <OnsaleTab />
            </Tab>
            <Tab key="trackasset" title="Track Asset">
              <TrackAssetTabs />
            </Tab>
            <Tab key="staking" title="Staking">
              <StakingTabs />
            </Tab>
          </Tabs>
        </div>
      </div>

      <ProfileEditModal
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        user={user}
        onClose={onClose}
        onSave={handleProfileSave}
      />
    </>
  );
};

export default ProfileComponents;
